import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';
import { MobileController } from '@phase2/outline-core';

import componentStyles from './pc-filter.css.lit';
import globalStyles from './pc-filter.lightDom.css.lit';
import '../pc-icon/pc-icon';

/**
 * Wysiwyg component
 * @element pc-filter
 * @slot - This element has a default slot
 */
@customElement('pc-filter')
export class PCFilter extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;
  static styles: CSSResultArray = [componentStyles];
  private mobileController = new MobileController(this);

  @property()
  label: string;

  @property({ type: Number, attribute: 'active-filters', reflect: true })
  activeFilters = 0;

  @property({ type: Boolean, reflect: true })
  open = false;

  @query('details')
  _details: HTMLDetailsElement;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('click', this._handleOutsideClick);
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);

    // Create no result element.
    const noResult = document.createElement('li');
    noResult.classList.add('facet-message');
    noResult.innerText = 'No result';

    // Calculate active filters.
    const activeOptions = this.querySelectorAll('a.is-active').length;
    this.setAttribute('active-filters', activeOptions.toString());

    // Implements filter options.
    const optionsWrapper = this.querySelector('.facet-items');
    const options = this.querySelectorAll(
      '.facet-item'
    ) as NodeListOf<HTMLLIElement>;
    const input = this.querySelector('.facet-search input') as HTMLInputElement;

    if (input) {
      /**
       * TODO: Refactor to handler method/remove from connectedCallback.
       */
      input.addEventListener('keyup', function (event) {
        const value = event?.target as HTMLInputElement;
        const searchText = (value.value ?? '').trim().toLowerCase();
        const list = this.parentElement?.nextElementSibling as HTMLElement;

        options.forEach(function (option) {
          const includesSearchText = (option?.textContent ?? '')
            .trim()
            .toLowerCase()
            .includes(searchText);

          if (!includesSearchText) {
            option.style.display = 'none';
          } else {
            if (option.hasAttribute('style')) {
              option.removeAttribute('style');
            }
          }
        });

        const isEmpty =
          list.querySelectorAll('.facet-item:not([style])').length === 0;

        if (isEmpty) {
          optionsWrapper?.append(noResult);
        } else {
          noResult.remove();
        }
      });
    }

    // Adds mutation attribute open observer to reset filtered items.
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (
          mutation.type === 'attributes' &&
          !(mutation.target as PCFilter).hasAttribute('open')
        ) {
          input.value = '';

          noResult.remove();
        }
      });
    });

    observer.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    window.removeEventListener('click', this._handleOutsideClick);
    super.disconnectedCallback();
  }

  _handleOutsideClick = (event: Event) => {
    if (this.open && !this.mobileController.isMobile) {
      if (!event.composedPath().includes(this)) {
        this.open = false;
      }
    }
  };

  _handleToggleEvent() {
    this.open = this._details.open;
  }

  renderSummaryLabel(): TemplateResult {
    if (this.activeFilters <= 0) {
      return html` ${this.label} `;
    }

    return html`
      <div>
        ${this.label} <span class="active-filter">${this.activeFilters}</span>
      </div>
    `;
  }

  render(): TemplateResult {
    return html`
      <details @toggle=${this._handleToggleEvent} ?open=${this.open}>
        <summary class="label">
          ${this.renderSummaryLabel()}
          <pc-icon name="chevron-down" is-fixed></pc-icon>
        </summary>
        <div class="content">
          <slot></slot>
        </div>
      </details>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-filter': PCFilter;
  }
}
